import { memo, useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Calendar as CalendarIcon, ChevronDown, ListTree, Loader2 } from 'lucide-react';
import type { Task } from '@/lib/schemas';
import { cn } from '@/lib/utils';
import { useTask } from '@/hooks/use-tasks';
import { useI18n } from '@/lib/i18n';

// ============================================
// CALENDAR EVENT CARD
// ============================================
//
// t16: a coloured "pill" that represents a single task on a calendar
// day. Designed to live inside a small day cell so the chrome stays
// minimal — title, an optional priority/status dot, and a left/right
// border that tells the user the event is part of a multi-day span.
//
// The pill is purely visual — drag + click are owned by the parent
// (calendar-day-cell.tsx, which wraps the pill in a dnd-kit
// useDraggable). That keeps the presentational layer reusable and the
// drag layer local.
//
// T16 R3: the pill is now larger (text-xs + py-1 / px-2) so it's
// easier to read in the calendar grid. It also gained a subtask-count
// chevron button (KanbanSubtaskPopover style) that opens a Radix
// popover listing the subtasks with title + status icon. Clicking a
// subtask row opens it via `onTaskClick` (same callback the pill
// itself uses).

export interface CalendarEventCardProps {
    task: Task;
    colorHex: string;
    /** True when this pill is the *first* day of a multi-day event. */
    isStart: boolean;
    /** True when this pill is the *last* day of a multi-day event. */
    isEnd: boolean;
    /** True when this pill is the *only* day of a multi-day event. */
    isSingleDay: boolean;
    onClick?: (task: Task) => void;
    /** When true, render a slightly translucent pill (drag preview, etc.). */
    overlay?: boolean;
}

const PRIORITY_DOT: Record<string, string> = {
    URGENT: 'bg-red-400',
    HIGH: 'bg-orange-400',
    MEDIUM: 'bg-amber-400',
    LOW: 'bg-sky-400',
    NONE: 'bg-transparent',
};

const STATUS_DOT: Record<string, string> = {
    TODO: 'bg-indigo-400',
    IN_PROGRESS: 'bg-blue-400',
    IN_REVIEW: 'bg-purple-400',
    DONE: 'bg-emerald-400',
    CANCELLED: 'bg-zinc-400',
};

function CalendarEventCardImpl({
    task,
    colorHex,
    isStart,
    isEnd,
    isSingleDay,
    onClick,
    overlay,
}: CalendarEventCardProps) {
    const { t } = useI18n();

    // Background tint is a low-opacity wash of the colour so adjacent
    // days still look like part of the same event. The 1px solid border
    // uses the full-saturation colour.
    const bg = overlay ? colorHex : `${colorHex}33`; // ~20% alpha
    const border = colorHex;
    const radius = cn(
        isSingleDay && 'rounded-md',
        isStart && !isEnd && 'rounded-l-md rounded-r-none',
        isEnd && !isStart && 'rounded-r-md rounded-l-none',
        !isSingleDay && !isStart && !isEnd && 'rounded-none'
    );
    const borderSides = cn(
        'border-t border-b',
        isStart && 'border-l',
        isEnd && 'border-r',
        !isStart && !isEnd && 'border-l border-r border-dashed'
    );

    return (
        <button
            type="button"
            onClick={(e) => {
                // Don't open the task detail panel when the user is
                // interacting with the subtask popover trigger (which
                // lives in a child element). The subtask button uses
                // its own stopPropagation in onClick.
                if (e.defaultPrevented) return;
                e.stopPropagation();
                onClick?.(task);
            }}
            className={cn(
                // T16 R3: bigger pill — text-xs + py-1 / px-2 / gap-1.5
                // reads more comfortably in the day cell without
                // crowding. `min-h-[28px]` keeps single-day cells
                // from collapsing when a pill only contains the
                // priority dot + title.
                'group/wrap relative w-full text-left px-2 py-1 text-xs leading-tight truncate min-h-[28px]',
                'hover:brightness-110 transition-[filter]',
                radius,
                borderSides,
                overlay && 'shadow-lg ring-2 ring-primary/40'
            )}
            style={{ backgroundColor: bg, borderColor: border, color: '#fff' }}
            data-testid={`calendar-event-${task.id}`}
            title={task.title}
        >
            <span className="flex items-center gap-1.5 truncate">
                <span
                    className={cn('inline-block h-1.5 w-1.5 rounded-full shrink-0', PRIORITY_DOT[task.priority] ?? 'bg-transparent')}
                    aria-hidden="true"
                />
                {task.dueDate && (
                    <CalendarIcon className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
                )}
                <span className="truncate">{task.title}</span>
                {task.subtaskCount > 0 && !overlay && (
                    <SubtaskCountButton task={task} onSelectSubtask={onClick} />
                )}
            </span>
        </button>
    );
}

// ============================================
// SUBTASK COUNT BUTTON
// ============================================
//
// T16 R3: chevron button at the right of the pill showing the subtask
// count. Opens a Radix popover with the subtask list. Mirrors
// KanbanSubtaskPopover — same stopPropagation-only-on-click pattern
// (we deliberately do NOT call preventDefault because Radix
// Popover.Trigger uses composeEventHandlers which short-circuits its
// own toggle when defaultPrevented is true, which would silently
// prevent the popover from opening — see the long R1 comment in
// kanban-subtask-popover.tsx for the full story).

function SubtaskCountButton({
    task,
    onSelectSubtask,
}: {
    task: Task;
    onSelectSubtask?: (task: Task) => void;
}) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    // Lazy fetch — only hits the network when the popover opens.
    // useTask hits /api/tasks/:id which returns the TaskDetail shape
    // (subtasks: Task[]). The data is cached at taskKeys.detail so
    // reopens are instant.
    const { data, isFetching, refetch } = useTask(open ? task.id : '');

    useEffect(() => {
        if (open) refetch();
    }, [open, refetch]);

    const subtasks = (data?.subtasks ?? []) as Task[];

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('calendar.subtasksLabel')}
                    className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] bg-black/30 hover:bg-black/50 transition-colors cursor-pointer"
                    onClick={(e) => {
                        // T21 R4 fix: don't call preventDefault() here —
                        // Popover.Trigger composes its onClick with the
                        // child's onClick via composeEventHandlers and
                        // SKIPS its own toggle handler when defaultPrevented
                        // is true, which silently kills the open call.
                        // (Same R1 fix KanbanSubtaskPopover applied.)
                        // stopPropagation alone is enough to keep the
                        // parent pill's onClick (which opens the task-
                        // detail panel) from firing.
                        e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpen((o) => !o);
                        }
                    }}
                    data-testid={`calendar-subtask-button-${task.id}`}
                >
                    <ListTree className="h-3 w-3" />
                    <span>{task.subtaskCount}</span>
                    <ChevronDown className="h-2.5 w-2.5 opacity-70" />
                </span>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    side="bottom"
                    align="end"
                    sideOffset={4}
                    className="z-50 min-w-[220px] max-w-[320px] rounded-md border border-border bg-surface p-1 shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-foreground-muted">
                        {t('calendar.subtasksLabel')} ({subtasks.length})
                    </p>
                    {isFetching && subtasks.length === 0 ? (
                        <div className="flex items-center gap-2 px-2 py-2 text-xs text-foreground-muted">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {t('common.loading')}
                        </div>
                    ) : subtasks.length === 0 ? (
                        <p className="px-2 py-2 text-xs text-foreground-muted">{t('calendar.subtasksEmpty')}</p>
                    ) : (
                        <ul className="space-y-0.5 max-h-64 overflow-y-auto">
                            {subtasks.map((sub) => (
                                <li key={sub.id}>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpen(false);
                                            onSelectSubtask?.(sub);
                                        }}
                                        className="flex w-full items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-surface-hover transition-colors"
                                    >
                                        <span
                                            className={cn(
                                                'inline-block h-2 w-2 rounded-full shrink-0',
                                                STATUS_DOT[sub.status] ?? 'bg-zinc-400'
                                            )}
                                            aria-hidden="true"
                                        />
                                        <span className="truncate flex-1">{sub.title}</span>
                                        <span className="text-[10px] uppercase tracking-wide text-foreground-muted shrink-0">
                                            {sub.status}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// Memoised so the same task list rendered on adjacent days doesn't
// re-render every pill on every drag.
export const CalendarEventCard = memo(CalendarEventCardImpl);